import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactComponent as CloseModalIcon } from '../../../../assets/svg/CloseModalIcon.svg'

const AutocompleteComponent = ({ options, selectedOptions, onSelect, disabled }) => {
    const { t } = useTranslation()
    const [showDropdown, setShowDropdown] = useState(false)
    const [emailValue, setEmailValue] = useState('')
    const [tags, setTags] = useState([])
    const [filteredOptions, setFilteredOptions] = useState(options)

    const dropdownRef = useRef(null)
    const inputRef = useRef(null)
    const containerRef = useRef(null)

    useEffect(() => {
        if (emailValue) {
            const filtered = options.filter((option) => option.toLowerCase().includes(emailValue.toLowerCase()) && !tags.includes(option))
            setFilteredOptions(filtered)
        } else {
            setFilteredOptions(options.filter((option) => !tags.includes(option)))
        }
    }, [emailValue, options, tags])

    useEffect(() => {
        if (!!selectedOptions) {
            setTags(selectedOptions)
            setFilteredOptions(options.filter((option) => !tags.includes(option)))
        }
    }, [selectedOptions, options])

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && emailValue.trim()) {
            if (options.includes(emailValue.trim()) && !tags.includes(emailValue.trim())) {
                const newTags = [...tags, emailValue.trim()]
                setTags(newTags)
                if (onSelect) {
                    onSelect(newTags)
                }
            }
            setEmailValue('')
            e.preventDefault()
            setShowDropdown(true)
        } else if (e.key === 'Backspace' && emailValue === '' && tags.length > 0) {
            const newTags = tags.slice(0, -1)
            setTags(newTags)
            if (onSelect) {
                onSelect(newTags)
            }
            setShowDropdown(true)
        }
    }

    const removeTag = (tagToRemove) => {
        const newTags = tags.filter((tag) => tag !== tagToRemove)
        setTags(newTags)
        if (onSelect) {
            onSelect(newTags)
        }
        inputRef.current.focus()
        setShowDropdown(true)
    }

    const handleOptionClick = (option, e) => {
        if (!tags.includes(option)) {
            const newTags = [...tags, option]
            setTags(newTags)
            if (onSelect) {
                onSelect(newTags)
            }
        }
        setEmailValue('')
        setShowDropdown(true)
        inputRef.current.focus()
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        if (containerRef.current) {
            const observer = new ResizeObserver(() => {
                if (showDropdown && dropdownRef.current && containerRef.current) {
                    const containerRect = containerRef.current.getBoundingClientRect()
                    if (dropdownRef.current) {
                        dropdownRef.current.style.top = `${containerRect.height - 18}px`
                    }
                }
            })

            observer.observe(containerRef.current)

            return () => {
                observer.disconnect()
            }
        }
    }, [showDropdown, tags])

    return (
        <div className="w-full relative" ref={containerRef}>
            <div className="mt-2 w-full min-h-10 border border-[#7C82C380] flex flex-1 items-center rounded-[10px] bg-white relative z-20 p-1 flex-wrap">
                {tags.map((tag, index) => (
                    <div key={index} className="bg-lightBG px-2 py-1 rounded-[10px] m-1 flex items-center">
                        <span className="text-[14px] font-medium">{tag}</span>
                        <button
                            type="button"
                            aria-label={`${t('remove')} ${tag}`}
                            className="ml-[6px] flex h-[18px] w-[18px] min-w-[18px] items-center justify-center rounded-full bg-darkPurple transition-opacity hover:opacity-80"
                            onClick={() => removeTag(tag)}
                        >
                            <CloseModalIcon height={7} width={7} />
                        </button>
                    </div>
                ))}

                <div className="flex-grow flex items-center h-[40px] min-w-[120px]">
                    <input
                        ref={inputRef}
                        className="outline-none h-5 font-semibold text-[14px] leading-5 w-full flex-1 px-2"
                        type="text"
                        placeholder={tags.length === 0 ? 'teacher@school.com, counsellor@school.com...' : ''}
                        value={emailValue}
                        onClick={() => setShowDropdown(true)}
                        onFocus={() => setShowDropdown(true)}
                        onChange={(e) => setEmailValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={disabled}
                        style={{ background: 'transparent' }}
                    />
                </div>
            </div>
            <div className="h-[12px]">{/* <p className="text-[11px] font-medium leading-[17px] text-[#413F5EE6]">{t('Press "ENTER" to save the word you want to add')}</p> */}</div>
            <div
                ref={dropdownRef}
                className="absolute left-0 right-0 z-10 overflow-y-auto transition-all duration-300 ease-in-out border-x border-[#7C82C380] bg-white rounded-b-[10px]"
                style={{
                    top: '0px',
                    maxHeight: showDropdown ? '130px' : '0px',
                    borderBottomWidth: showDropdown ? '1px' : '0px',
                    opacity: showDropdown ? 1 : 0,
                }}
            >
                <div className="flex pt-[8px] flex-col">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => (
                            <div
                                key={index}
                                className="pl-[14px] py-[10px] pr-[10px] w-full flex flex-row items-center hover:bg-lightBG cursor-pointer"
                                onClick={(e) => handleOptionClick(option, e)}
                            >
                                <p className="font-medium text-[14px] leading-5">{option}</p>
                            </div>
                        ))
                    ) : (
                        <div className="pl-[14px] py-[10px] pr-[10px] flex flex-row items-center justify-center">
                            <p className="text-gray-500">{t('No matching options')}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default AutocompleteComponent
